import {RESOURCE_PAGE_REQUEST} from '../providers/http-context-tokens';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from "rxjs/operators";
import { ResponseErrorsHandlerService } from "../services/response-errors-handler.service";
import { JwtService } from "../services/jwt.service";
import { TokenDataProviderService } from "../services/token-data-provider.service";
import { Router } from "@angular/router";
import {environment} from '../../../environments/environment';

@Injectable()
export class JwtHttpInterceptor implements HttpInterceptor {

  constructor(
    private reh: ResponseErrorsHandlerService,
    private jwt: JwtService,
    private ts: TokenDataProviderService,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isApiRequest(request)) return next.handle(request);
    this.jwt.getTokenFromStorage();
    const isRefreshRequest = this.isRefreshRequest(request);
    const sentToken = this.jwt.token;
    const sentSession = this.jwt.sessionRevision;

    return next.handle(this.addToken(request, this.jwt.token)).pipe(catchError(error => {
      request.context.get(RESOURCE_PAGE_REQUEST)?.(error);
      if (!isPlatformBrowser(this.platformId)) {
        return throwError(() => error);
      }
      this.jwt.getTokenFromStorage();
      if (this.jwt.sessionRevision !== sentSession || (isRefreshRequest && this.jwt.token !== sentToken)) {
        return throwError(() => error);
      }
      // Anonymous requests cannot signal an expired authenticated session.
      if (error instanceof HttpErrorResponse && error.status === 401
        && !sentToken && !isRefreshRequest && !this.jwt.canRefresh()) {
        return throwError(() => error);
      }
      if (
        error instanceof HttpErrorResponse
        && !isRefreshRequest
        && error.status === 401
        && this.jwt.canRefresh()
      ) {
        // A late 401 may belong to a request sent before another refresh finished.
        if (this.jwt.token && this.jwt.token !== sentToken) {
          return this.retryRequest(request, next, this.jwt.token);
        }
        return this.jwt.refreshJwtToken().pipe(
          switchMap(response => this.retryRequest(request, next, response.token))
        );
      }

      if (
        error instanceof HttpErrorResponse
        && (
          (!!sentToken && !this.jwt.canRefresh() && error.status === 401)
          || (isRefreshRequest && (error.status === 401 || error.status === 400))
        )
      ) {
        if (this.jwt.token || this.jwt.hasRemember()) {
          this.ts.logout(true);
          this.router.navigate(['/']);
        }
      }

      this.notifyError(request, error);
      return throwError(() => error);
    }));
  }

  private retryRequest(request: HttpRequest<any>, next: HttpHandler, token: string): Observable<HttpEvent<any>> {
    return next.handle(this.addToken(request, token)).pipe(catchError(error => {
      request.context.get(RESOURCE_PAGE_REQUEST)?.(error);
      // A resource-specific 401 after a successful refresh is not proof that
      // the refresh token is invalid. Other HTTP failures still show their toast.
      if (error.status !== 401) this.notifyError(request, error);
      return throwError(() => error);
    }));
  }


  private notifyError(request: HttpRequest<any>, error: HttpErrorResponse): void {
    if (request.context.get(RESOURCE_PAGE_REQUEST) && (error.status === 403 || error.status === 404)) return;
    this.reh.handleServerErrors(error, request.method);
  }

  private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    if (this.isRefreshRequest(request)) {
      return request;
    }

    if (!token) {
      return request;
    }

    return request.clone({
      setHeaders: {
        'Authorization': `Bearer ${token}`
      }
    });
  }

  private isRefreshRequest(request: HttpRequest<any>): boolean {
    return request.url.split('?')[0].endsWith('/token/refresh');
  }

  private isApiRequest(request: HttpRequest<any>): boolean {
    const base = new URL(environment.apiUrl, environment.appUrl);
    const target = new URL(request.url, environment.appUrl);
    return target.origin === base.origin
      && (target.pathname === base.pathname || target.pathname.startsWith(base.pathname.replace(/\/$/, '') + '/'));
  }
}
